import { useCallback, useEffect, useRef, useState } from 'react';
import {
  DEFAULT_SHORTCUT_BINDINGS,
  SHORTCUT_DEFINITIONS,
  type ShortcutBinding,
  type ShortcutBindings,
  type ShortcutId,
  type ShortcutModifier,
} from './keyboardShortcutLogic';

const STORAGE_PREFIX = 'zunelo:keyboard-shortcuts:';
const CHANGE_EVENT = 'zunelo:keyboard-shortcuts-changed';
const MODIFIERS: readonly ShortcutModifier[] = [
  'none',
  'primary',
  'alt',
  'shift',
  'primary-shift',
  'alt-shift',
  'primary-alt',
  'primary-alt-shift',
];

interface ShortcutBindingsChangeDetail {
  key: string;
  bindings: ShortcutBindings;
}

function cloneDefaults(): ShortcutBindings {
  return Object.fromEntries(
    SHORTCUT_DEFINITIONS.map(({ id }) => [id, { ...DEFAULT_SHORTCUT_BINDINGS[id] }]),
  ) as ShortcutBindings;
}

function storageKey(userId?: string): string {
  return `${STORAGE_PREFIX}${userId || 'anonymous'}`;
}

function isBinding(value: unknown): value is ShortcutBinding {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<ShortcutBinding>;
  return typeof candidate.key === 'string' && candidate.key.length > 0 && MODIFIERS.includes(candidate.modifier as ShortcutModifier);
}

function readBindings(userId?: string): ShortcutBindings {
  if (typeof window === 'undefined') return cloneDefaults();
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) return cloneDefaults();
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return cloneDefaults();
    const candidate = parsed as Partial<Record<ShortcutId, unknown>>;
    const next = cloneDefaults();
    for (const { id } of SHORTCUT_DEFINITIONS) {
      if (isBinding(candidate[id])) next[id] = { ...candidate[id] };
    }
    return next;
  } catch {
    return cloneDefaults();
  }
}

function persistBindings(userId: string | undefined, bindings: ShortcutBindings): boolean {
  if (typeof window === 'undefined') return false;
  let persisted = false;
  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(bindings));
    persisted = true;
  } catch {
    // Preferences remain usable for the current session when storage is unavailable.
  }
  window.dispatchEvent(
    new CustomEvent<ShortcutBindingsChangeDetail>(CHANGE_EVENT, {
      detail: { key: storageKey(userId), bindings },
    }),
  );
  return persisted;
}

export function formatShortcutBinding(binding: ShortcutBinding): string {
  const key = binding.key === ' ' ? 'Space' : binding.key.length === 1 ? binding.key.toUpperCase() : binding.key;
  const modifier = binding.modifier;
  if (modifier === 'none') return key;
  if (modifier === 'primary') return `⌘/Ctrl + ${key}`;
  if (modifier === 'alt') return `Alt + ${key}`;
  if (modifier === 'shift') return `Shift + ${key}`;
  if (modifier === 'primary-shift') return `⌘/Ctrl + Shift + ${key}`;
  if (modifier === 'alt-shift') return `Alt + Shift + ${key}`;
  if (modifier === 'primary-alt') return `⌘/Ctrl + Alt + ${key}`;
  return `⌘/Ctrl + Alt + Shift + ${key}`;
}

export function bindingFromKeyboardEvent(event: KeyboardEvent): ShortcutBinding | null {
  if (['Alt', 'Control', 'Meta', 'Shift'].includes(event.key)) return null;
  let modifier: ShortcutModifier = 'none';
  if (event.metaKey || event.ctrlKey) {
    if (event.altKey && event.shiftKey) modifier = 'primary-alt-shift';
    else if (event.altKey) modifier = 'primary-alt';
    else if (event.shiftKey) modifier = 'primary-shift';
    else modifier = 'primary';
  }
  else if (event.altKey) modifier = event.shiftKey ? 'alt-shift' : 'alt';
  else if (event.shiftKey) modifier = 'shift';
  return { key: event.key, modifier };
}

export function useKeyboardShortcutSettings(userId?: string) {
  const [bindings, setBindings] = useState<ShortcutBindings>(() => readBindings(userId));
  const bindingsRef = useRef(bindings);
  const key = storageKey(userId);

  useEffect(() => {
    const next = readBindings(userId);
    bindingsRef.current = next;
    setBindings(next);
    const onChange = (event: Event) => {
      const detail = (event as CustomEvent<ShortcutBindingsChangeDetail>).detail;
      const changed = detail?.key === key ? detail.bindings : readBindings(userId);
      bindingsRef.current = changed;
      setBindings(changed);
    };
    window.addEventListener(CHANGE_EVENT, onChange);
    return () => window.removeEventListener(CHANGE_EVENT, onChange);
  }, [key, userId]);

  const updateBinding = useCallback(
    (id: ShortcutId, binding: ShortcutBinding) => {
      const next = { ...bindingsRef.current, [id]: binding };
      bindingsRef.current = next;
      setBindings(next);
      return persistBindings(userId, next);
    },
    [userId],
  );

  const resetBindings = useCallback(() => {
    const next = cloneDefaults();
    bindingsRef.current = next;
    setBindings(next);
    return persistBindings(userId, next);
  }, [userId]);

  return { bindings, updateBinding, resetBindings };
}
